import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { formatDateBr } from '@core/utils/filter';
import SLSPicService from '@/services/Operacoes/SLSPic.service';
import SLSPicItemService from '@/services/Operacoes/SLSPicItem.service';
// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import _ from 'lodash';

export default function usePicList(nameModule) {
  // Use toast
  const toast = useToast();
  const sLSPicService = new SLSPicService();
  const sLSPicItemService = new SLSPicItemService();
  const refPicListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'picid', label: 'Pic', sortable: false },
    { key: 'picdescricao', label: 'Descrição', sortable: true },
    { key: 'tipopicid', label: ' Pic', sortable: true },
    // { key: 'valortotal', label: 'Valor Total', sortable: true },
    { key: 'picdata', label: 'Data Cadastro', sortable: true },
    { key: 'piciniciovalidade', label: 'Inicio Validade', sortable: true },
    { key: 'picterminovalidade', label: 'Termino Validade', sortable: true },
    { key: 'picsituacao', label: 'Situação', sortable: true },
    { key: 'actions', label: 'Ações', sortable: false },
  ];
  const perPage = ref(10);
  const totalPic = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('picid');
  const isSortDirDesc = ref(false);
  const statusFilter = ref(null);
  const isLoading = ref(false);

  const dataMeta = computed(() => {
    const localItemsCount = refPicListTable.value ? refPicListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPic.value,
    };
  });

  const refetchData = () => {
    refPicListTable.value.refresh();
  };

  // Watchers para rastrear alterações
  watch([currentPage, perPage, searchQuery, statusFilter], () => {
    cachedResults.value = {
      data: null,
      timestamp: null,
    };
    refetchData();
  }, { deep: true });

  // Mecanismo de cache
  const cachedResults = ref({
    data: null,
    timestamp: null,
  });

  const cacheTTL = 10 * 60 * 1000; // TTL de 10 minutos

  const isCacheValid = () => {
    if (!cachedResults.value.timestamp) return false;
    const now = new Date().getTime();
    return now - cachedResults.value.timestamp < cacheTTL;
  };

  const fetchPic = (ctx, callback) => {
    isLoading.value = true;

    const shouldUseCache = isCacheValid()
            && cachedResults.value.currentPage === currentPage.value
            && cachedResults.value.perPage === perPage.value;

    if (cachedResults.value.data && shouldUseCache) {
      callback(cachedResults.value.data);
      isLoading.value = false;
      return;
    }

    sLSPicService.index({
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      fullObject: true,
    }).then((response) => {
      const { data, total } = response;
      callback(data);
      perPage.value = response.perPage;
      currentPage.value = response.page;
      totalPic.value = total;

      cachedResults.value = {
        data,
        timestamp: new Date().getTime(),
        currentPage: currentPage.value,
        perPage: perPage.value,
      };
      isLoading.value = false;
    })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Pic list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
        isLoading.value = false;
      });
  };

  const create = async (body) => {
    try {
      await sLSPicService.store(body);

      cachedResults.value = {
        data: null,
        timestamp: null,
      };

      toast({
        component: ToastificationContent,
        props: {
          title: 'Sucesso',
          text: 'Pic cadastrada com sucesso!',
          icon: 'CheckIcon',
          variant: 'success',
        },
      });

      refetchData();
    } catch (error) {
      console.error('Erro ao cadastrar Pic.');
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro',
          text: 'Erro ao cadastrar Pic.',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
    }
  };

  const Updated = async (body) => {
    try {
      const itemsResponse = await sLSPicItemService.index({ idpic: body.picid, perPage: 1 });

      if (itemsResponse.total === 0) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Atenção',
            text: 'Essa PIC está sem itens. Adicione itens para enviar para aprovação.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      body.picsituacao = 'Pendente';
      body.picobservacaoaprovador = '';
      await sLSPicService.update(body.picid, body);

      cachedResults.value = {
        data: null,
        timestamp: null,
      };

      toast({
        component: ToastificationContent,
        props: {
          title: 'Sucesso',
          text: 'PIC enviada para aprovação!',
          icon: 'CheckIcon',
          variant: 'success',
        },
      });

      refetchData();
    } catch (error) {
      console.error('Erro ao enviar PIC para aprovação:', error);
      toast({
        component: ToastificationContent,
        props: {
          title: 'Erro',
          text: 'Ocorreu um erro ao tentar enviar a PIC para aprovação.',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      });
    }
  };

  const removerData = async (id) => {
    await sLSPicService.destroy(id);
    cachedResults.value = {
      data: null,
      timestamp: null,
    };
    refetchData();
  };

  const resolveTypeFieldText = type => {
    if (type === 'VARCHAR') return 'Texto';
    if (type === 'BOOLEAN') return 'Verdadeiro/Falso';
    if (type === 'DATE') return 'Data';
    if (type === 'DATETIME') return 'Data e Hora';
    if (type === 'DECIMAL') return 'Monetário';
    if (type === 'TEXT') return 'Texto Longo';
    if (type === 'INTEGER' || type === 'INT') return 'Númerico';
    return 'Sem ';
  };

  const resolveTypeFieldIcon = type => {
    if (type === 'VARCHAR') return 'tabler:abc';
    if (type === 'BOOLEAN') return 'Verdadeiro/Falso';
    if (type === 'DATE') return 'system-uicons:calendar-date';
    if (type === 'DATETIME') return 'formkit:datetime';
    if (type === 'TEXT') return 'mdi:text-long';
    if (type === 'DECIMAL') return 'tabler:decimal';
    if (type === 'DECIMAL' || type === 'INTEGER' || type === 'INT') return 'tabler:123';
    return 'nonicons:type-16';
  };

  const resolveItemStatusVariant = (status) => {
    if (status === 'PENDING') return 'light-warning';
    if (status === 'ATIVO') return 'light-success';
    if (status === 'INATIVO') return 'light-danger';
    return 'primary';
  };

  return {
    fetchPic,
    tableColumns,
    perPage,
    currentPage,
    totalPic,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPicListTable,
    isLoading,
    resolveTypeFieldText,
    resolveTypeFieldIcon,
    resolveItemStatusVariant,
    create,
    Updated,
    removerData,
  };
}
